import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import { Section } from 'store/returns/models/returnData';

export const OSS_RETURN_TYPE_ID = (process.env.OSS_ID || '').trim();

export const isOssReturnType = (returnTypeId?: string | null): boolean =>
  !!OSS_RETURN_TYPE_ID &&
  !!returnTypeId &&
  returnTypeId === OSS_RETURN_TYPE_ID;

/** Collapse case, spacing, and common separators for section label comparison. */
export const normalizeOssSectionLabel = (label: string): string =>
  label.trim().toLowerCase().replace(/[\s_-]+/g, '');

/** True when a section name is the Out of Scope bucket (not an OSS country). */
export const isOssOutOfScopeSectionName = (
  sectionName?: string | null
): boolean => {
  if (!sectionName) return false;

  return normalizeOssSectionLabel(sectionName) === 'outofscope';
};

export const getMappedSubsectionCodesFromJsonMapping = (
  jsonMapping: MappingTaxRate[] | undefined
): Set<string> => {
  const mappedSubsectionCodes = new Set<string>();

  jsonMapping?.forEach((entry) => {
    entry.codes
      ?.split(',')
      .map((code) => code.trim())
      .filter(Boolean)
      .forEach((code) => mappedSubsectionCodes.add(code));
  });

  return mappedSubsectionCodes;
};

/** True when `code` matches any of the removed OSS subsection codes (incl. FR4 vs FR4(VAT)). */
export const isOssCodeAmongRemoved = (
  code: string,
  removedSubsectionCodes: Iterable<string>
): boolean =>
  [...removedSubsectionCodes].some((removed) =>
    ossBoxCodeMatchesMappedCode(removed, code)
  );

/**
 * Strip removed-country box codes from the persisted mapping payload so hydrate
 * cannot resurrect side-panel tags after a country is removed in the drawer.
 */
export const pruneCodesFromJsonMapping = (
  jsonMapping: MappingTaxRate[] | undefined,
  removedSubsectionCodes: Iterable<string>
): MappingTaxRate[] | undefined => {
  if (!jsonMapping?.length) return jsonMapping;

  const removed = [...removedSubsectionCodes];
  if (removed.length === 0) return jsonMapping;

  return jsonMapping.map((entry) => {
    if (!entry.codes) return entry;

    const remaining = entry.codes
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code && !isOssCodeAmongRemoved(code, removed));

    const nextCodes = remaining.join(',');
    if (nextCodes === entry.codes) return entry;

    return {
      ...entry,
      codes: nextCodes,
    };
  });
};

export const normalizeOssBoxCode = (code: string) =>
  code.trim().toUpperCase().replace(/\(VAT\)$/, '');

export const ossBoxCodeMatchesMappedCode = (
  rowBoxCode: string,
  mappedCode: string
): boolean => {
  const row = normalizeOssBoxCode(rowBoxCode);
  const mapped = normalizeOssBoxCode(mappedCode);

  return mapped === row || mapped === `${row}VAT`;
};

export const ossCountryHasMappedSubsection = (
  rowBoxCodes: string[],
  mappedCodes: Iterable<string>
): boolean => {
  const mappedList = [...mappedCodes];
  if (mappedList.length === 0) return false;

  return rowBoxCodes.some((rowBoxCode) =>
    mappedList.some((mappedCode) =>
      ossBoxCodeMatchesMappedCode(rowBoxCode, mappedCode)
    )
  );
};

export const MAPPING_PAGE_PATH_PATTERN =
  /^\/returns\/[^/]+(\/editMapping\/[^/]+|\/tifdMapping(\/[^/]+)?)?$/;

export const isMappingPagePath = (pathname: string): boolean =>
  MAPPING_PAGE_PATH_PATTERN.test(pathname);

export const isLeavingMappingPage = (
  currentPathname: string,
  nextPathname: string
): boolean =>
  isMappingPagePath(currentPathname) && !isMappingPagePath(nextPathname);

type RateWithLocations = { location: string[] };

export const getMappedSubsectionCodesFromSelectedRates = (
  selectedRates: RateWithLocations[] | undefined
): Set<string> => {
  const mappedSubsectionCodes = new Set<string>();

  selectedRates?.forEach((rate) => {
    rate.location.forEach((code) => {
      if (code) mappedSubsectionCodes.add(code);
    });
  });

  return mappedSubsectionCodes;
};

/** OSS country sections selected in the drawer but with no mapped subsection codes. */
export const getUnmappedOssCountrySections = (
  selectedOssCountryIds: string[],
  sections: Section[] | undefined,
  returnTypeId: string | undefined,
  mappedSubsectionCodes: Set<string>
): Section[] => {
  if (
    !isOssReturnType(returnTypeId) ||
    !sections?.length ||
    selectedOssCountryIds.length === 0
  ) {
    return [];
  }

  return sections
    .filter((section) => section.returnTypeId === returnTypeId)
    .filter((section) => selectedOssCountryIds.includes(section.id))
    .filter(
      (section) =>
        !section.subsections.some(
          (subsection) =>
            !subsection.outOfScope &&
            [...mappedSubsectionCodes].some((mappedCode) =>
              ossBoxCodeMatchesMappedCode(subsection.code, mappedCode)
            )
        )
    );
};

/** True when at least one in-scope subsection in the section has a mapped box code. */
export const isMappingSectionCompleted = (
  section: Section,
  mappedSubsectionCodes: Set<string>
): boolean => {
  const inScopeRowBoxCodes = section.subsections
    .filter((subsection) => !subsection.outOfScope)
    .map((subsection) => subsection.code);

  if (inScopeRowBoxCodes.length === 0) return true;

  return ossCountryHasMappedSubsection(
    inScopeRowBoxCodes,
    mappedSubsectionCodes
  );
};

/** Section IDs for OSS countries that have at least one mapped subsection code. */
export const deriveOssCountryIdsFromJsonMapping = (
  jsonMapping: MappingTaxRate[] | undefined,
  sections: Section[] | undefined,
  returnTypeId: string | undefined
): string[] => {
  if (
    !isOssReturnType(returnTypeId) ||
    !jsonMapping?.length ||
    !sections?.length
  ) {
    return [];
  }

  const mappedSubsectionCodes =
    getMappedSubsectionCodesFromJsonMapping(jsonMapping);

  if (mappedSubsectionCodes.size === 0) return [];

  return sections
    .filter((section) => section.returnTypeId === returnTypeId)
    .filter((section) =>
      section.subsections.some(
        (subsection) =>
          !subsection.outOfScope &&
          [...mappedSubsectionCodes].some((mappedCode) =>
            ossBoxCodeMatchesMappedCode(subsection.code, mappedCode)
          )
      )
    )
    .map((section) => section.id);
};
